// KitConfirmed.jsx - The page somebody lands on after asking for a kit.
//
// It is a real route rather than a state inside the form for two reasons. An
// advertising platform can be told "a conversion is somebody reaching this URL",
// which is the setup every ad interface expects and the one nobody has to
// maintain. And a person who bookmarks it, refreshes it, or hits back arrives
// somewhere that still makes sense.
//
// It is `noindex` on purpose. A thank you page in a search result is somebody
// landing on a receipt for something they never did, which is why the whole
// industry keeps these out of the index. Google Ads does not need a page indexed
// to count it.
//
// The second job of this page is the one the form never had room for. Somebody
// who has just asked for a kit has three to five days of waiting ahead of them
// and a genuine appetite to understand what their gold is worth, so this is the
// best moment on the whole site to hand them the calculators and the guides.
//
// The copy here states what the tools do and stops. An earlier draft opened with
// "nothing here is a sales page", which denies an accusation nobody made and
// reads as exactly the thing it is denying.
//
// The timeline it shows is `WhatHappensNext` in parts.jsx, shared with the
// dialog somebody gets if they fill the form in a second time. Two accounts of
// our own turnaround that disagree is worse than either of them.
//
// LEGEND
//   1. KitConfirmed   the page, with and without a request behind it
//   2. HeardAbout     the one question we ask, after the request is already in
//   3. Resources      where to send somebody who is now waiting

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import clsx from "clsx";

import { Seo, JsonLd, breadcrumbSchema } from "../lib/seo.jsx";
import { ARTICLES } from "../lib/content.js";
import * as analytics from "../lib/analytics.js";
import { remembered } from "../lib/session.js";
import { publicApi } from "../lib/api.js";
import { HEARD_LABELS } from "../lib/format.js";
import { Panel, Button, Input } from "../ui/kit.jsx";
import { CheckCircle, ArrowRight, Scale, Note, Dollar, Check } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { WhatHappensNext } from "./parts.jsx";

////////// 1. THE PAGE //////////

export default function KitConfirmed() {
  const { state } = useLocation();
  const submitted = state?.order || null;
  const fired = useRef(false);

  // A refresh or a bookmark loses the navigation state, and this page used to
  // become a generic "looking for your order?" for somebody who had asked
  // ninety seconds earlier. The browser kept the order when the form
  // succeeded, so read it back. Read after mount rather than during render:
  // this page is prerendered, and localStorage does not exist at that point.
  const [kept, setKept] = useState(null);
  useEffect(() => {
    if (!submitted) setKept(remembered());
  }, [submitted]);

  const order = submitted || (kept && { number: kept.number, token: kept.token, trackingUrl: `/track/${kept.token}` });

  // The conversion is reported here rather than at the moment the form posted,
  // so the event and the URL agree. It only fires for somebody who actually
  // arrived from a submission, never for a refresh or a bookmark, and it carries
  // the id the server sent its own copy with so the two count once.
  useEffect(() => {
    // Only for a real submission. A refresh that recovered the order from this
    // browser is not a second conversion and must never be counted as one.
    if (!submitted || fired.current) return;
    fired.current = true;
    analytics.trackLead("kit_request", { order_id: submitted.number });
  }, [submitted, state]);

  return (
    <>
      <Seo
        title="Your request is in"
        path="/get-a-kit/confirmed"
        description="Your free kit request has been received. Here is what happens next and roughly when."
        noIndex
      />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Get a free kit", to: "/get-a-kit" },
        { label: "Request received", to: "/get-a-kit/confirmed" },
      ])} />

      <div className="shell-narrow pt-12 md:pt-20 pb-12">
        <Reveal className="text-center">
          <CheckCircle size={42} className="mx-auto text-positive mb-6" />
          <h1 className="display display-lg text-ink">
            {order ? "Your request is in." : "Thanks, your request is in."}
          </h1>
          <p className="lede mt-5 measure-wide mx-auto text-muted">
            Nothing else is needed from you right now. We will email and text you the moment your kit ships.
          </p>
        </Reveal>

        {order ? (
          <Panel className="p-5 sm:p-6 mt-9">
            <p className="eyebrow">Your reference</p>
            <p className="display display-md text-ink mt-1.5 num">{order.number}</p>
            <p className="hint mt-4 leading-relaxed">
              This link stays current for the whole process. It is where the video, the itemised weights and your
              offer will appear. We have emailed it to you as well.
            </p>
            <a href={order.trackingUrl} className="btn btn-primary w-full mt-5">
              Open my tracking page <ArrowRight size={16} />
            </a>
          </Panel>
        ) : (
          // Somebody refreshed, or came back to this address later. The page still
          // has to be useful rather than an error about missing state.
          <Panel className="p-5 sm:p-6 mt-9">
            <p className="eyebrow">Looking for your order?</p>
            <p className="hint mt-2 leading-relaxed">
              Your tracking link is in the email we sent. If you cannot find it, we will send it again to the address
              or the phone number you used.
            </p>
            <Link to="/track" className="btn btn-quiet w-full mt-4">
              Find my order <ArrowRight size={16} />
            </Link>
          </Panel>
        )}

        <WhatHappensNext className="mt-10" />

        <HeardAbout token={submitted?.token || kept?.token} />

        <p className="hint mt-9 text-center">
          Questions in the meantime? Call{" "}
          <a href="tel:+1-401-315-4653" className="link text-ink font-semibold">(401) 315-4653</a>{" "}
          or just reply to the email.
        </p>
      </div>

      <Resources />
    </>
  );
}

////////// 2. WHERE THEY FOUND US //////////
// The only attribution we have that a person actually chose.
//
// It is here and not on the form, and that is the whole design. A question
// between somebody and the thing they came to do costs conversions, and an
// answer given to get past a question is worth nothing anyway. Here the request
// is already in, nothing depends on the answer, and the page says so.
//
// So it is genuinely optional. No default tile, no required field, no nagging
// if they ignore it, and the page below carries on regardless. One tap is the
// whole interaction; the text box underneath is for the person who wants to say
// more, and is never in the way of the person who does not.
//
// A failure is swallowed. Somebody who has just given us their gold must never
// see an error about our marketing survey.

function HeardAbout({ token }) {
  const [choice, setChoice] = useState(null);
  const [detail, setDetail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!token) return null;

  const send = async (source, note) => {
    setBusy(true);
    try {
      await publicApi.sayWhereTheyHeard(token, { source, detail: note || undefined });
      setDone(true);
    } catch {
      // Nothing they did wrong and nothing they can fix. Thank them anyway
      // rather than putting a red box on the page after a conversion.
      setDone(true);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Panel className="p-5 sm:p-6 mt-8">
        <p className="flex items-center gap-2 text-[14.5px] font-semibold text-ink">
          <Check size={16} className="text-positive" /> Thank you, that genuinely helps.
        </p>
        <p className="hint mt-1.5">It tells us where to spend so more people find us the same way.</p>
      </Panel>
    );
  }

  return (
    <Panel className="p-5 sm:p-6 mt-8">
      <p className="eyebrow">One optional question</p>
      <h2 className="display display-sm text-ink mt-1.5">How did you find us?</h2>
      <p className="hint mt-2 leading-relaxed">
        Nothing depends on this and your request is already in. It just tells us which of the things
        we do are worth doing.
      </p>

      <div className="flex flex-wrap gap-2 mt-5">
        {Object.entries(HEARD_LABELS).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={choice === value}
            onClick={() => {
              setChoice(value);
              // "Somewhere else" is the one answer that is useless on its own,
              // so it waits for the box. Everything else sends on the tap: one
              // gesture, done, and nobody has to find a submit button.
              if (value !== "other") send(value);
            }}
            className={clsx(
              "btn btn-sm",
              choice === value ? "btn-ink" : "btn-quiet"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {choice === "other" && (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Input
            label="Where was it?"
            placeholder="A podcast, a neighbour, a shop window"
            value={detail}
            onChange={(event) => setDetail(event.target.value)}
            wrapClassName="flex-1 min-w-[14rem]"
          />
          <Button onClick={() => send("other", detail)} loading={busy}>Send</Button>
        </div>
      )}
    </Panel>
  );
}

////////// 3. WHILE YOU WAIT //////////
// The kit takes two or three days to arrive, and this is the only point in the
// whole process where somebody is both interested and has nothing to do. Sending
// them to the calculators now is what makes the offer conversation easy later:
// a customer who already knows what a gram of 14K contains does not need to be
// argued with about a deduction.

const TOOLS = [
  { to: "/tools/gold-calculator", icon: Scale, title: "Work out your own numbers", body: "Weight in, pure gold content and value at spot out." },
  { to: "/tools/karat-chart", icon: Note, title: "What the mark means", body: "417, 585, 750, 925, PLAT, GF and EPNS, with the metal content of each. Worth two minutes before you pack." },
  { to: "/prices", icon: Dollar, title: "Today's metal prices", body: "Live gold, silver, platinum and palladium, per ounce and per gram, updated through the day." },
];

function Resources() {
  // The three we have marked as the ones worth reading first.
  const guides = ARTICLES.filter((article) => article.featured).slice(0, 3);

  return (
    <>
      <section className="band border-t border-rule bg-surface/40">
        <div className="shell">
          <div className="mb-8 md:mb-10">
            <p className="eyebrow mb-3">While you wait</p>
            <h2 className="display display-lg text-ink">Two or three days is enough to get ahead of us.</h2>
            <p className="lede mt-4 measure text-muted">
              The calculators run the same arithmetic we will, against today's spot price. Ten minutes with them and
              you will know what your pieces contain before the mailer lands.
            </p>
          </div>

          <StaggerBoxed as="div" step={90} className="grid gap-px bg-rule border border-rule sm:grid-cols-3" itemClassName="bg-paper h-full">
            {TOOLS.map(({ to, icon: Glyph, title, body }) => (
              <Link key={to} to={to} className="group block bg-paper hover:bg-surface transition-colors p-5 sm:p-6">
                <Glyph size={20} className="text-bronze mb-3" />
                <h3 className="display display-sm text-ink group-hover:text-bronze-dark transition-colors">{title}</h3>
                <p className="hint mt-2 leading-relaxed">{body}</p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-ink">
                  Open it <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </StaggerBoxed>
        </div>
      </section>

      {guides.length > 0 && (
        <section className="band">
          <div className="shell">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="eyebrow mb-3">Reading</p>
                <h2 className="display display-lg text-ink">How this industry actually works.</h2>
              </div>
              <Link to="/learn" className="btn btn-quiet">All the guides <ArrowRight size={16} /></Link>
            </div>

            <StaggerBoxed as="div" step={90} className="grid gap-8 md:grid-cols-3">
              {guides.map((article) => (
                <Link key={article.slug} to={`/learn/${article.slug}`} className="group block">
                  <p className="eyebrow mb-2">{article.category} &middot; {article.readMinutes} min</p>
                  <h3 className="display display-sm text-ink group-hover:text-bronze-dark transition-colors">
                    {article.title}
                  </h3>
                  <p className="hint mt-2 leading-relaxed line-clamp-3">{article.description}</p>
                  <span className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-ink">
                    Read <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </StaggerBoxed>
          </div>
        </section>
      )}

    </>
  );
}
